import type * as React from "react";
import { useTranslation } from "react-i18next";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StudioButton } from "@/features/studio/theme";
import { formatRupees } from "@/lib/format";

export interface ProposalDecision {
	mode: "accept" | "reject";
	designerName: string;
	costEstimate: number;
}

interface ProposalDecisionDialogProps {
	decision: ProposalDecision | null;
	onConfirm: () => void;
	onCancel: () => void;
}

/**
 * Asks the homeowner to confirm hiring or declining a designer's proposal.
 * `onCancel` also runs after a confirm, because the dialog closes either way;
 * `onConfirm` runs first and still sees the decision.
 */
export function ProposalDecisionDialog({
	decision,
	onConfirm,
	onCancel,
}: ProposalDecisionDialogProps): React.ReactElement {
	const { t } = useTranslation();
	const isAccept = decision?.mode === "accept";

	return (
		<AlertDialog
			open={decision !== null}
			onOpenChange={(open) => {
				if (!open) onCancel();
			}}
		>
			{decision && (
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{isAccept
								? t("proposalDecision.acceptTitle", {
										name: decision.designerName,
									})
								: t("proposalDecision.rejectTitle")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{isAccept
								? t("proposalDecision.acceptBody", {
										name: decision.designerName,
										cost: formatRupees(decision.costEstimate),
									})
								: t("proposalDecision.rejectBody", {
										name: decision.designerName,
									})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
						{isAccept ? (
							<AlertDialogAction
								onClick={onConfirm}
								className="border-none bg-transparent text-dashboard-on-fill hover:bg-transparent dashboard-primary-button"
							>
								{t("proposalDecision.acceptConfirm")}
							</AlertDialogAction>
						) : (
							<AlertDialogAction
								asChild
								className="focus-visible:outline-solid"
							>
								<StudioButton
									variant="secondary"
									onClick={onConfirm}
									style={{
										paddingLeft: 16,
										paddingRight: 16,
										fontSize: 14,
										borderRadius: "var(--radius-md)",
										border: "1px solid var(--color-dashboard-border)",
									}}
								>
									{t("proposalDecision.rejectConfirm")}
								</StudioButton>
							</AlertDialogAction>
						)}
					</AlertDialogFooter>
				</AlertDialogContent>
			)}
		</AlertDialog>
	);
}
